import { createNanoBananaRunner, getNanoBananaParams } from './nanoBanana';
import { cropOutpaintSource, getOutpaintSourceDimensions } from './outpaintSource';
import type { ModelRunCredits } from '@/api/modelPrediction';
import type { CompositorImageSource } from '@/components/Recipe/Views/CompositorView';
import type { ResolvedModelPrice } from '@/components/Nodes/Utils';
import type { CompositorAiRun } from './CompositorAiTasks';
import type { CompositorAiSubmitResult, LayerId, OutpaintPadding, OutpaintSourceCrop } from 'web';
import type { ImageEditRunner } from './nanoBanana';

export const OUTPAINT_ASPECT_RATIOS = [
  { label: '21:9', value: 21 / 9 },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '1:1', value: 1 },
  { label: '2:3', value: 2 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '9:16', value: 9 / 16 },
  { label: '9:21', value: 9 / 21 },
];

const OUTPAINT_SYSTEM_PROMPT = `Image 1 is the full-resolution visible source image. Image 2 is the outpaint guide for the extended canvas.
Use all of image 1 as the source of image content. Match image 2's full canvas framing, aspect ratio, and exact placement and size of the source image.
Replace the checkerboard area in image 2 with a seamless extension, matching the original style, lighting, and perspective. Follow the user's instructions when provided; otherwise extend the scene naturally.
Preserve the original image content exactly. Return only the completed image, without guide markings or borders.`;

export interface OutpaintParams {
  prompt: string;
  groupName: string;
  padding: OutpaintPadding;
  sourceCrop?: OutpaintSourceCrop;
}

export interface OutpaintOptions {
  groupName: string;
  modelLabel: string;
  getPrice: (padding: OutpaintPadding, sourceCrop?: OutpaintSourceCrop) => ResolvedModelPrice | undefined;
}

export function createOutpaintAction({ groupName, modelLabel, getPrice }: OutpaintOptions) {
  return {
    modelLabel,
    getPrice,
    createRun(prompt: string, padding: OutpaintPadding, sourceCrop?: OutpaintSourceCrop): CompositorAiRun {
      return { action: 'outpaint', params: { prompt, groupName, padding, sourceCrop } };
    },
  };
}

export type OutpaintAction = ReturnType<typeof createOutpaintAction>;

export function getOutpaintParams(
  width: number,
  height: number,
  padding: OutpaintPadding,
  sourceCrop?: OutpaintSourceCrop,
) {
  ({ width, height } = getOutpaintSourceDimensions(width, height, sourceCrop));
  const longestEdge = Math.max(width * (1 + padding.left + padding.right), height * (1 + padding.top + padding.bottom));
  const resolution = longestEdge <= 1024 * 1.25 ? '1K' : longestEdge <= 2048 * 1.25 ? '2K' : '4K';
  return { ...getNanoBananaParams(width, height), resolution };
}

export function createOutpaintRunner(
  getSource: (layerId: LayerId) => CompositorImageSource | undefined,
  prepareGuide: () => Extract<CompositorAiSubmitResult, { type: 'outpaint' }> | undefined,
  onCredits?: (credits: ModelRunCredits) => void,
): ImageEditRunner {
  return (target, observation) => {
    const submission = prepareGuide();
    const padding = submission?.padding;
    const systemPrompt = padding
      ? `${OUTPAINT_SYSTEM_PROMPT}
Extend the original image by these fractions: left ${padding.left.toFixed(4)} and right ${padding.right.toFixed(4)} of its width; top ${padding.top.toFixed(4)} and bottom ${padding.bottom.toFixed(4)} of its height.
These are fractions of image 1's dimensions, not of the final canvas. Keep the original image at that exact offset within the expanded canvas.`
      : OUTPAINT_SYSTEM_PROMPT;
    return createNanoBananaRunner(
      'outpaint',
      systemPrompt,
      getSource,
      () => submission?.guide,
      onCredits,
      (width, height) => getOutpaintParams(width, height, submission!.padding),
      (source, signal) => cropOutpaintSource(source, submission?.sourceCrop, signal),
    )(target, observation);
  };
}
